import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'chart.js/auto';
import style from './style.module.css';
import { ResponseDataGet } from '../../../../models/responseDataGet';
import { EntSummary } from '../api/types';

interface AnimalData {
  name: string;
  value: number;
  color: string;
}

interface LeftGraphProps {
  data?: ResponseDataGet<EntSummary>;
  isLoading?: boolean;
}

export const LeftGraph: React.FC<LeftGraphProps> = ({ data, isLoading }) => {
    const { t } = useTranslation();
    const chartRef = useRef<HTMLCanvasElement | null>(null);
    const chartInstance = useRef<Chart | null>(null);
    
    // Цвета для категорий животных
    const colorMap: Record<string, string> = {
        'КРС': '#006400',
        'Овцы и козы': '#32CD32',
        'Лошади': '#E0F0A0',
        'Домашняя птица': '#FFA500',
        'Пчелосемьи': '#FFD700',
        'Свиньи': '#FF69B4',
        'Осел': '#90EE90',
        'Верблюд': '#964B00',
        'Кролик': '#FFB6C1',
        'Үй канаттуулары': '#FFA500', 
    };

    // Универсальный способ получить массив категорий
    const categories: any[] = (data?.res && (data.res as any).categories) || data?.res?.content || [];

    // Формируем данные для диаграммы из API данных
    const animalData: AnimalData[] = React.useMemo(() => {
        if (!data?.success || !categories || categories.length === 0) {
            return [];
        }
        return categories.map((category: any) => ({
            name: category.nameRu,
            value: Math.round(category.percent),
            color: colorMap[category.nameRu] || '#' + Math.floor(Math.random()*16777215).toString(16)
        }));
    }, [data, categories]);

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
            if (animalData.length > 0) {
                const ctx = chartRef.current.getContext('2d');
                if (ctx) {
                    chartInstance.current = new Chart(ctx, {
                        type: 'doughnut',
                        data: {
                            labels: animalData.map(item => item.name),
                            datasets: [
                                {
                                    data: animalData.map(item => item.value),
                                    backgroundColor: animalData.map(item => item.color),
                                    hoverOffset: 4
                                }
                            ]
                        },
                        options: {
                            responsive: true,
                            maintainAspectRatio: false,
                            cutout: '70%',
                            plugins: {
                                legend: {
                                    display: false
                                },
                                tooltip: {
                                    callbacks: {
                                        label: (context) => {
                                            const label = context.label || '';
                                            const value = context.raw as number;
                                            const dataIndex = context.dataIndex;
                                            const category = categories[dataIndex];
                                            if (category) {
                                                return `${label}: ${category.count} (${value}%)`;
                                            }
                                            return `${label}: ${value}%`;
                                        }
                                    }
                                }
                            }
                        }
                    });
                }
            }
        }
        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [animalData, data, categories]);

    // Вычисляем сумму животных для отображения в центре диаграммы
    const totalCount = React.useMemo(() => {
        if (data?.success && categories && categories.length > 0) {
            return categories.reduce((sum: number, category: any) => sum + category.count, 0);
        }
        return 0;
    }, [data, categories]);

    if (isLoading) {
        return (
            <div className={style.container}>
                <h2 className={style.title}>{t('Диаграмма Скота на карте')}</h2>
                <div className={`${style.chartContainer} ${style.loading}`}>
                    <div>Загрузка...</div>
                </div>
            </div>
        );
    }

    if (!data?.success || !categories || categories.length === 0) {
        return (
            <div className={style.container}>
                <h2 className={style.title}>{t('Диаграмма Скота на карте')}</h2>
                <div className={`${style.chartContainer} ${style.loading}`}>
                    <div>{data?.msg || 'Нет данных для отображения'}</div>
                </div>
            </div>
        );
    }

    return (
        <div className={style.container}>
            <h2 className={style.title}>{t('Диаграмма Скота на карте')}</h2>
            <div className={style.chartContainer}>
                <div className={style.chartRow}>
                    <div className={style.chartWrapper}>
                        <canvas ref={chartRef}></canvas>
                        <div className={style.centerText}>
                            {/* {totalCount} */}
                            100%
                            </div>
                    </div>
                    <div className={style.legend}>
                        {animalData.map((animal, index) => (
                            <div key={index} className={style.legendItem}>
                                <div className={style.colorIndicator} style={{ backgroundColor: animal.color }}></div>
                                <span>{animal.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};